import { useOutletContext, useRouteLoaderData } from '@remix-run/react';

import CopyrightText from '~/components/CopyrightText';
import { PrivacyPolicy, TermsOfUse } from '~/components/Policies';
import { PoweredByBeehiiv } from '~/components/PoweredByBeehiiv';

import type { OutletContext } from '~/interfaces/outlet_context';

import type { ProprietaryFooterProps } from './ProprietaryFooter.types';

import type { RootLoaderData } from '~/root';

const ProprietaryFooter = ({
  node,
  showPoweredByBeehiiv = true,
  shouldRenderOverContent = false,
}: ProprietaryFooterProps) => {
  const { site } = useRouteLoaderData('root') as RootLoaderData;

  const { publicationName, publicationCopyrightText } = useOutletContext<OutletContext>();

  if (!site.publication.beehiiv_branding) return null;

  const textColor = node?.attrs?.color ?? node?.attrs?.backgroundColor;
  const textClassName = !node?.attrs?.color ? 'invert' : undefined;

  const body = (
    <div
      className="flex w-full flex-col items-center gap-y-2 bg-transparent py-2 sm:py-0 sm:flex-row sm:justify-between sm:gap-x-2 md:px-0 sm:px-2"
      style={{ backgroundColor: shouldRenderOverContent ? 'transparent' : node?.attrs?.backgroundColor }}
    >
      <div className="w-full text-center sm:w-1/2 sm:text-left sm:whitespace-nowrap mt-2 sm:mt-0">
        <CopyrightText
          color={textColor}
          text={publicationCopyrightText || publicationName || ''}
          className={textClassName}
        />
      </div>

      <div className="flex w-full flex-col items-center gap-y-4 sm:w-1/2 sm:flex-row sm:justify-end sm:gap-x-4">
        <div className="flex gap-x-2 sm:gap-x-2 sm:whitespace-nowrap">
          <PrivacyPolicy
            color={textColor}
            className={textClassName}
            privacyPolicyUrl={site.viewable_site_version.settings?.privacy_policy_url}
          />
          <TermsOfUse
            color={textColor}
            className={textClassName}
            termsOfUseUrl={site.viewable_site_version.settings?.terms_of_use_url}
          />
        </div>

        {showPoweredByBeehiiv && <PoweredByBeehiiv color={textColor} className={textClassName} />}
      </div>
    </div>
  );

  return shouldRenderOverContent ? (
    <div className="absolute bottom-2 left-1/2 z-10 -translate-x-1/2 transform text-center sm:bottom-4 sm:left-4 sm:translate-x-0">
      {body}
    </div>
  ) : (
    body
  );
};

export { ProprietaryFooter };
